import React from 'react';
import { Button, Input, Label, Row, Col } from 'reactstrap';
import Icon from '@mdi/react';
import { mdiAccountOutline } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import HolidayAnalysisCalendarMonthly from '../../../components/Holiday/Analysis/HolidayAnalysisCalendarMonthly/HolidayAnalysisCalendarMonthly';
import HolidayAnalysisCalendarYearly from '../../../components/Holiday/Analysis/HolidayAnalysisCalendarYearly/HolidayAnalysisCalendarYearly';
import Legend from '../../../components/Holiday/Analysis/components/Legend';
import useTableHook from '../../../components/hooks/useTableHook';
import { AuthUtil } from '../../../utils';
import { fetchHolidayCalendarDetails } from '../../../store/apis/holidayService';
import {
  fetchTableColumnValues,
  fetchHRCodeList,
} from '../../../store/apis/commonService';
import { fetchEmployeesAutoComplete } from '../../../store/apis/autocompleteService';
import {
  MONTHS,
  YEARS,
  REPORTING_TYPES,
  CALENDAR_OPTIONS,
  LAST_DATE,
} from './constants';
import { getYearRange, getMonthRange } from './utils';
import {
  UserAsyncAutocompleteSelect,
  TableFilterBubbles,
  CustomSelect,
} from '../../../components/Common';

import './Analysis.scss';

export default function Analysis() {
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth(); // 0-11
  const { startDate, endDate } = getMonthRange(thisMonth, thisYear);
  const {
    data,
    pageRequest,
    externalFilters,
    setExternalFilters,
    applyExternalFilters,
    setPageRequest,
    setColumnName,
    columnData,
    applyFilter,
    columnName,
    resetFilters,
    handleManualSort,
    checkColumnIfSorted,
    isLoadingData,
    isLoadingColumn,
    clearSingleFilterValue,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      reportingType: REPORTING_TYPES.MONTHLY,
      managerId: AuthUtil.getAuthUser()?.employeeId,
      pageNumber: 1,
      pageSize: 0,
      sortColumn: 'Dept',
      sortDirection: 'ASC',
      filters: [],
      fromDate: startDate,
      toDate: endDate,
      isDirectReport: 0,
      isViewAllEmployee: 0,
      ddShowOpt: CALENDAR_OPTIONS.ACTUAL,
      year: thisYear,
      month: thisMonth,
    },
    fetchFn: fetchHolidayCalendarDetails,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'holidayAnalysis',
    columnKeyPrefix: 'holidayAnalysisColumn',
    tableName: 'HolidayAnalysis',
    defaultExternalFilters: {
      managerId: {
        label: AuthUtil.getAuthUser()?.userFullName,
        value: AuthUtil.getAuthUser()?.employeeId,
      },
      reportingType: REPORTING_TYPES.MONTHLY,
      year: thisYear,
      month: thisMonth,
      ddShowOpt: CALENDAR_OPTIONS.ACTUAL,
    },
  });

  const { data: departmentList } = useQuery({
    queryKey: ['departmentList'],
    queryFn: () =>
      fetchHRCodeList({
        type: 'DEPT_ACTIVE',
      }),
    enabled: true,
    keepPreviousData: true,
  });

  const { data: sectionList } = useQuery({
    queryKey: ['sectionList'],
    queryFn: () =>
      fetchHRCodeList({
        type: 'SECTION_ACTIVE',
      }),
    enabled: true,
    keepPreviousData: true,
  });

  const handleViewCalendar = (
    month = null,
    year = null,
    setExternal = false,
  ) => {
    let period = {};
    if (externalFilters?.reportingType === REPORTING_TYPES.MONTHLY) {
      period = getMonthRange(
        month ?? +(externalFilters?.month ?? 0),
        year ??
          (externalFilters?.year !== undefined
            ? +externalFilters.year
            : undefined),
      );
    } else if (externalFilters?.reportingType === REPORTING_TYPES.YEARLY) {
      period = getYearRange(year ?? externalFilters?.year);
    }
    const newExtFilters = {
      ...externalFilters,
      fromDate: period.startDate,
      toDate: LAST_DATE,
      month: month ?? externalFilters?.month,
      year: year ?? externalFilters?.year,
    };
    applyExternalFilters({
      ...newExtFilters,
      employeeId: externalFilters?.employeeId?.value ?? '',
      managerId: externalFilters?.managerId?.value ?? '',
    });
    if (setExternal) {
      setExternalFilters(newExtFilters);
    }
  };

  const handleMonthNext = () => {
    let nextMonth = +(externalFilters?.month ?? 0) + 1;
    let year = externalFilters?.year;
    if (nextMonth > 11) {
      nextMonth = 0;
      year += 1;
    }
    handleViewCalendar(nextMonth, year, true);
  };

  const handleMonthPrev = () => {
    let prevMonth = +(externalFilters?.month ?? 0) - 1;
    let year = externalFilters?.year;
    if (prevMonth < 0) {
      year -= 1;
      prevMonth = 11;
    }
    handleViewCalendar(prevMonth, year, true);
  };

  const handleResetExternalFilter = () => {
    setExternalFilters({
      ...externalFilters,
      departmentId: '',
      section: '',
      employeeId: null,
      managerId: {
        label: AuthUtil.getAuthUser()?.userFullName,
        value: AuthUtil.getAuthUser()?.employeeId,
      },
      isDirectReport: 0,
      isViewAllEmployee: 0,
      reportingType: REPORTING_TYPES.MONTHLY,
      month: thisMonth,
      year: thisYear,
      ddShowOpt: CALENDAR_OPTIONS.ACTUAL,
    });
  };

  return (
    <div className="page-content px-0 analysis-page">
      <div className="header-container mb-2 d-flex justify-content-center align-items-center py-2">
        <span className="fw-semibold">
          Holiday Analysis – {pageRequest?.year || new Date().getFullYear()}
        </span>
      </div>
      <div className=" d-flex flex-column px-3">
        <div className="external-filter-container">
          <Row className="mb-2">
            <Col>
              <Label for="department" className="mb-1">
                <span>Department</span>
                <span className="text-danger ms-1">*</span>
              </Label>
              <CustomSelect
                options={departmentList?.map(department => ({
                  value: department?.Code?.trim(),
                  label: department?.Description,
                }))}
                onChange={e => {
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    departmentId: e,
                  }));
                }}
                value={externalFilters?.departmentId}
                placeholder="Select a Department"
                className="external-filter-input"
              />
            </Col>
            <Col>
              <Label for="departmentSelect" className="mb-1">
                <span>Section</span>
                <span className="text-danger ms-1">*</span>
              </Label>
              <CustomSelect
                options={sectionList?.map(section => ({
                  value: section?.Code?.trim(),
                  label: section?.Description,
                }))}
                onChange={e => {
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    section: e,
                  }));
                }}
                value={externalFilters?.section}
                placeholder="Select a Section"
                className="external-filter-input"
              />
            </Col>
            <Col>
              <div>
                <Label for="holidayTypeSelect" className="mb-1">
                  Manager
                </Label>
                <UserAsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchEmployeesAutoComplete({
                      prefixText: input,
                      type: 'MANAGER',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Cd,
                      label: item.Name,
                      name: item.Name,
                      code: item.Cd,
                      designation: item.Designation,
                      image: item?.Imagefile,
                    }))
                  }
                  onChange={e => {
                    setExternalFilters(prevFilters => ({
                      ...prevFilters,
                      managerId: e,
                    }));
                  }}
                  placeholder="Select manager"
                  isClearable
                  isSearchable
                  value={externalFilters?.managerId}
                  inputIcon={
                    <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                  }
                  rightKeyAssessor="code"
                  searchEmployee={
                    AuthUtil.getAuthUser()?.employeeId?.toLowerCase() !==
                    AuthUtil.getAuthUser()?.userName?.toLowerCase()
                  }
                />
              </div>
              <div className="d-flex align-items-center gap-2 mt-2">
                <Input
                  type="checkbox"
                  id="isDirectReporting"
                  className="mb-2 blue-checkbox"
                  checked={externalFilters?.isDirectReport === 1}
                  onChange={e => {
                    setExternalFilters(prevFilters => ({
                      ...prevFilters,
                      isDirectReport: e.target.checked ? 1 : 0,
                    }));
                  }}
                />
                <Label for="directReporting" className="mb-2">
                  Direct Reporting
                </Label>
              </div>
            </Col>
            <Col>
              <div>
                <Label for="holidayTypeSelect" className="mb-1">
                  Employee
                </Label>
                <UserAsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchEmployeesAutoComplete({
                      prefixText: input,
                      type: 'EMPLOYEE',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Cd,
                      label: item.Name,
                      name: item.Name,
                      code: item.Cd,
                      designation: item.Designation,
                      image: item?.Imagefile,
                    }))
                  }
                  onChange={e => {
                    setExternalFilters(prevFilters => ({
                      ...prevFilters,
                      employeeId: e,
                    }));
                  }}
                  placeholder="Select Employee"
                  isClearable
                  isSearchable
                  value={externalFilters?.employeeId}
                  inputIcon={
                    <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                  }
                  rightKeyAssessor="code"
                />
              </div>
              <div className="d-flex align-items-center gap-2 mt-2">
                <Input
                  type="checkbox"
                  id="isDirectReporting"
                  className="mb-2 blue-checkbox"
                  checked={externalFilters?.isViewAllEmployee === 1}
                  onChange={e => {
                    setExternalFilters(prevFilters => ({
                      ...prevFilters,
                      isViewAllEmployee: e.target.checked ? 1 : 0,
                    }));
                  }}
                />
                <Label for="directReporting" className="mb-2">
                  View All Employees
                </Label>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <Label for="reportingType" className="mb-1">
                Reporting Type
              </Label>
              <Input
                type="select"
                id="reportingType"
                value={externalFilters?.reportingType}
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    reportingType: e.target.value,
                  }))
                }
                className="external-filter-input"
              >
                <option>Select Reporting Type</option>
                <option value={REPORTING_TYPES.MONTHLY}>Monthly</option>
                <option value={REPORTING_TYPES.YEARLY}>Yearly</option>
              </Input>
            </Col>
            <Col className="d-flex gap-2">
              <div className="w-50">
                <Label for="period" className="mb-1">
                  Period
                </Label>
                <Input
                  type="select"
                  id="month"
                  value={externalFilters?.month}
                  onChange={e =>
                    setExternalFilters({
                      ...externalFilters,
                      month: e.target.value,
                    })
                  }
                  className="external-filter-input"
                  disabled={
                    externalFilters?.reportingType === REPORTING_TYPES.YEARLY
                  }
                >
                  <option value="">Select Month</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="w-50">
                <Label for="year" className="mb-1">
                  Year
                </Label>
                <Input
                  type="select"
                  id="year"
                  value={externalFilters?.year}
                  onChange={e =>
                    setExternalFilters({
                      ...externalFilters,
                      year: +e.target.value,
                    })
                  }
                  className="external-filter-input"
                >
                  <option value="">Select Year</option>
                  {YEARS.map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>
            <Col>
              <Label for="showCalendarBasedOn" className="mb-1">
                Show Calendar Based On
              </Label>
              <Input
                type="select"
                id="showCalendarBasedOn"
                value={externalFilters?.ddShowOpt}
                onChange={e =>
                  setExternalFilters({
                    ...externalFilters,
                    ddShowOpt: e.target.value,
                  })
                }
                className="external-filter-input"
              >
                <option>Select Calendar Type</option>
                <option value={CALENDAR_OPTIONS.ACTUAL}>Actual</option>
                <option value={CALENDAR_OPTIONS.PLAN}>Plan</option>
              </Input>
            </Col>
            <Col className="d-flex align-items-end gap-2">
              <Button
                className="custom-light-btn w-50"
                onClick={handleResetExternalFilter}
              >
                Reset
              </Button>
              <Button
                className="custom-btn w-50"
                style={{ textWrap: 'nowrap' }}
                onClick={() => handleViewCalendar()}
              >
                View Calendar
              </Button>
            </Col>
          </Row>
        </div>
        <div className="d-flex justify-content-between mt-2">
          <Legend type={pageRequest?.ddShowOpt} />
        </div>
        <div className="mb-2 mt-1">
          <TableFilterBubbles
            allFilters={pageRequest?.filters || []}
            clearSingleFilterValue={clearSingleFilterValue}
            clearAllFilters={resetFilters}
          />
        </div>
        {pageRequest?.reportingType === 'monthly' && (
          <HolidayAnalysisCalendarMonthly
            holidayData={data}
            year={pageRequest?.year}
            month={pageRequest?.month}
            handleMonthNext={handleMonthNext}
            handleMonthPrev={handleMonthPrev}
            pageRequest={pageRequest}
            setColumnName={setColumnName}
            columnData={columnData}
            applyFilter={applyFilter}
            columnName={columnName}
            setPageRequest={setPageRequest}
            handleManualSort={handleManualSort}
            checkColumnIfSorted={checkColumnIfSorted}
            isLoadingData={isLoadingData}
            isLoadingColumn={isLoadingColumn}
            handleAutoSort={handleAutoSort}
          />
        )}
        {pageRequest?.reportingType === 'yearly' && (
          <HolidayAnalysisCalendarYearly
            holidayData={data}
            selectedYear={pageRequest?.year}
            pageRequest={pageRequest}
            setColumnName={setColumnName}
            columnData={columnData}
            applyFilter={applyFilter}
            columnName={columnName}
            setPageRequest={setPageRequest}
            handleManualSort={handleManualSort}
            checkColumnIfSorted={checkColumnIfSorted}
            isLoadingData={isLoadingData}
            isLoadingColumn={isLoadingColumn}
            handleAutoSort={handleAutoSort}
          />
        )}
      </div>
    </div>
  );
}
